import React, { useState } from 'react';
import { DatoCmsAdvisorLeadFormSectionComponent } from '../../../schema/datocms';
import { getColumnSpanClass, getColumnStartClass, getTextAlignmentClass } from '../../../utilities';
import HtmlEntityTextComponent from '../../html-entity-text/html-entity-text-component';
import HtmlStringComponent from '../../html-string/html-string-component';
import { BhtpAdvisorLeadForm } from '../../advisor-lead-form/advisor-lead-form';
import { graphql, useStaticQuery } from 'gatsby';
import { AppConfig } from '../../../environment/app-config.model';
import Icon from '../../icon/icon';

export const EnvironmentStaticQuery = graphql`query EnvironmentStaticQuery {
    environment: environmentJson {
        bhtpConsumerGatewayBaseUrl
        recaptchaSiteKey
    }
}`;

interface ApiPartnerLead {
    id: string | undefined;
    partnerFirstName: string;
    partnerLastName: string;
    partnerTitle: string;
    partnerEmail: string;
    partnerPhone: string;
    agencyName: string;
    iataNumber: number;
    agencyWeb: string;
    homeBased: boolean | undefined;
    agencyAddress: string;
    agencyCity: string;
    agencyPostalCode: string;
    agencyState: string;
    agencyCountry: string;
}

interface ApiPartnerLeadRequest {
    token: string;
    lead: ApiPartnerLead;
}

interface FormPartnerLead {
    firstName: string;
    lastName: string;
    title: string;
    email: string;
    phone: string;
    agencyName: string;
    iataNumber: number;
    website: string;
    homeBased: boolean | undefined;
    address: string;
    postalCode: string;
    city: string;
    state: string;
    country: string;
 }

interface EnvironmentStaticQueryResult {
    environment: Partial<AppConfig>;
}

const AdvisorLeadFormSectionComponent = (
    { columnLayout, title, titleAlignment, descriptionHtml, descriptionAlignment
    }: DatoCmsAdvisorLeadFormSectionComponent
): React.ReactElement => {
    const { environment } = useStaticQuery<EnvironmentStaticQueryResult>(EnvironmentStaticQuery);

    // give the form a unique key, so it can be destroyed and recreated when a submit is successful
    const [formKey, setFormKey] = useState(0);

    const [submitting, setSubmitting] = useState<boolean>(false);
    const [successful, setSuccessful] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);

    const errorSectionId: string = 'partner-form-error';

    function submitted(data: any): void {
        if (submitting) {
            return;
        }

        setHasError(false);
        setSuccessful(false);
        setSubmitting(true);

        const formData: FormPartnerLead = data.detail as FormPartnerLead;

        grecaptcha.ready(() => {
            grecaptcha.execute(environment.recaptchaSiteKey || '', { action: 'partner_lead_form'})
                .then((token: string) => {
                    const req: ApiPartnerLeadRequest = {
                        token,
                        lead: {
                            id: undefined,
                            partnerFirstName: formData.firstName,
                            partnerLastName: formData.lastName,
                            partnerTitle: formData.title,
                            partnerEmail: formData.email,
                            partnerPhone: formData.phone,
                            agencyName: formData.agencyName,
                            iataNumber: formData.iataNumber,
                            agencyWeb: formData.website,
                            homeBased: formData.homeBased,
                            agencyAddress: formData.address,
                            agencyCity: formData.city,
                            agencyPostalCode: formData.postalCode,
                            agencyState: formData.state,
                            agencyCountry: formData.country
                        }
                    };

                    fetch(`${environment.bhtpConsumerGatewayBaseUrl}partners/lead`, {
                        method: 'POST',
                        cache: 'no-cache',
                        headers: {
                            'content-type': 'application/json'
                        },
                        body: JSON.stringify(req)
                    }).then((resp) => {
                        const success: boolean = resp.ok;
                        setSubmitting(false);
                        if(success) {
                            setFormKey((prevKey) => prevKey + 1);
                            setSuccessful(success);
                        } else {
                            setHasError(!success);
                        }
                    }).catch(() => {
                        setSubmitting(false);
                        setHasError(true);
                    });
                });
        });
    }

    const gridColumnSpanClass: string = getColumnSpanClass(columnLayout);
    const gridColumnStartClass: string = getColumnStartClass(columnLayout);

    return (
        <div className="theme-white page-component">
            <div className="container mx-auto lg:grid lg:grid-cols-12">
                <div className={`lg:${gridColumnSpanClass} lg:${gridColumnStartClass}`}>
                    { title && <h2 className={`${getTextAlignmentClass(titleAlignment)} mb-8`}>{ <HtmlEntityTextComponent text={title} /> }</h2> }
                    { descriptionHtml && <HtmlStringComponent className={`${getTextAlignmentClass(descriptionAlignment)} mb-8`} htmlString={descriptionHtml} skipAlignmentClasses={true} /> }

                    <BhtpAdvisorLeadForm key={formKey} config={{postalCodeValidationUrl: `${environment.bhtpConsumerGatewayBaseUrl}jurisdictions/cities/postalcode/`}} onCallToActionSubmitted={submitted}/>

                    { submitting && <div className="flex justify-center items-center space-x-2 text-primary"><Icon name="data_saver_off" className="fill-current animate-spin w-8 h-8" /><span>Submitting</span></div> }

                    <p className="text-brand-gray text-xs text-center pt-4">This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy">Privacy Policy</a> and <a href="https://policies.google.com/terms">Terms of Service</a> apply.</p>

                    { hasError &&
                        <div id={errorSectionId} className="flex flex-col justify-center text-center mt-16 text-error font-bold mb-8">
                            <span>Something went wrong while submitting the form. Please try again in a few minutes, or contact us at <a href="mailto:bhtpsales@bhspecialty.com">bhtpsales@bhspecialty.com</a>.</span>
                        </div>
                    }

                    { successful &&
                        <div className="flex flex-col justify-center text-center mt-16">
                            <h3 className="uppercase">Thank You!</h3>
                            <span>Your information has been submitted. One of our representatives will contact you within two business days.</span>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

export default AdvisorLeadFormSectionComponent;
